/**
 * Bringing the CK Doctor desktop service up before a page uses the hardware
 * attached to it (EDC terminal, smart-card reader, document scanner).
 *
 * The browser cannot see whether the desktop service is running, so every
 * hardware feature has to (1) probe it, (2) ask Windows/macOS to start it via
 * the `ckdoctor://` handler, and (3) wait for it to finish booting. Each
 * feature used to open-code that, and each got it wrong in the same way: a
 * single retry three seconds after the launch. Electron needs longer than that
 * on a cold machine, so the page declared "ไม่สามารถเปิดโปรแกรมได้" while the
 * service was still starting, and the user had to open it by hand.
 *
 * Notes for callers:
 * - The probe is `GET /health`. Do NOT probe with a device command — the old
 *   smart-card check used `POST /smart-cards`, which starts a real card read.
 * - Concurrent callers share one launch, so a page with two hardware widgets
 *   raises a single browser permission prompt instead of two.
 */

export const DESKTOP_SERVICE_URL = 'http://localhost:5985'
export const DESKTOP_PROTOCOL_URL = 'ckdoctor://'

/** One probe. Short: an unreachable local port fails almost immediately. */
export const DESKTOP_PROBE_TIMEOUT_MS = 2000
/** Gap between probes while the service boots. */
export const DESKTOP_POLL_INTERVAL_MS = 1000
/**
 * How long to wait for a launch. Electron cold start measured ~2 s on a warm
 * developer machine; a hospital PC with on-access antivirus scanning a 200 MB
 * executable is far slower, so this is deliberately generous — the user sees a
 * progress message the whole time and can cancel.
 */
export const DESKTOP_LAUNCH_TIMEOUT_MS = 45000

export const DESKTOP_SERVICE_PHASE = {
  probing: 'probing',
  launching: 'launching',
  waiting: 'waiting',
  ready: 'ready',
  offline: 'offline',
  canceled: 'canceled',
}

export const DESKTOP_PHASE_TEXT = {
  [DESKTOP_SERVICE_PHASE.probing]: 'กำลังตรวจสอบโปรแกรม CK Doctor Desktop...',
  [DESKTOP_SERVICE_PHASE.launching]: 'กำลังเปิดโปรแกรม CK Doctor Desktop...',
  [DESKTOP_SERVICE_PHASE.waiting]:
    'กำลังรอโปรแกรม CK Doctor Desktop เริ่มทำงาน...',
  [DESKTOP_SERVICE_PHASE.ready]: 'เชื่อมต่อโปรแกรม CK Doctor Desktop แล้ว',
  [DESKTOP_SERVICE_PHASE.offline]: 'ไม่พบโปรแกรม CK Doctor Desktop',
  [DESKTOP_SERVICE_PHASE.canceled]: 'ยกเลิกการเปิดโปรแกรมแล้ว',
}

export const DESKTOP_OFFLINE_MESSAGE =
  'เปิดโปรแกรม CK Doctor Desktop ไม่สำเร็จ ' +
  'กรุณาเปิดโปรแกรมด้วยตนเอง (Start Menu > CK Doctor Manager) แล้วลองใหม่อีกครั้ง'

export const normalizeDesktopUrl = (url = DESKTOP_SERVICE_URL) =>
  String(url || DESKTOP_SERVICE_URL).replace(/\/+$/, '')

/** True when the desktop service answers its health check. Never throws. */
export const pingDesktopService = async (
  baseUrl = DESKTOP_SERVICE_URL,
  timeoutMs = DESKTOP_PROBE_TIMEOUT_MS
) => {
  const controller =
    typeof AbortController === 'undefined' ? null : new AbortController()
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null
  try {
    const res = await fetch(`${normalizeDesktopUrl(baseUrl)}/health`, {
      method: 'GET',
      cache: 'no-store',
      ...(controller ? { signal: controller.signal } : {}),
    })
    return res?.ok === true
  } catch (e) {
    return false
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Hand `ckdoctor://` to the OS. On Windows the handler is registered by the
 * installer (HKLM) and re-registered by the service at runtime (HKCU); before
 * both existed this navigation was a no-op on a machine where the service had
 * never run.
 */
export const openDesktopApp = () => {
  if (typeof window === 'undefined' || !window.location) return false
  window.location.href = DESKTOP_PROTOCOL_URL
  return true
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const report = (onStatus, phase) => {
  if (!onStatus) return
  try {
    onStatus({ phase, text: DESKTOP_PHASE_TEXT[phase] || '' })
  } catch (e) {
    /* a broken status callback must not abort the launch */
  }
}

const result = (ok, phase, launched) => ({ ok, phase, launched })

// baseUrl -> { promise, listeners } so overlapping callers share one launch.
const pendingLaunches = new Map()

const launchAndWait = async ({
  url,
  listeners,
  timeoutMs,
  pollIntervalMs,
  probeTimeoutMs,
  launch,
}) => {
  const notify = (phase) => listeners.forEach((l) => report(l, phase))

  notify(DESKTOP_SERVICE_PHASE.launching)
  launch()

  const deadline = Date.now() + timeoutMs
  notify(DESKTOP_SERVICE_PHASE.waiting)

  while (Date.now() < deadline) {
    await delay(pollIntervalMs)
    if (await pingDesktopService(url, probeTimeoutMs)) {
      notify(DESKTOP_SERVICE_PHASE.ready)
      return result(true, DESKTOP_SERVICE_PHASE.ready, true)
    }
  }

  notify(DESKTOP_SERVICE_PHASE.offline)
  return result(false, DESKTOP_SERVICE_PHASE.offline, true)
}

/**
 * Resolve once the desktop service is reachable, launching it if needed.
 *
 * Resolves `{ ok, phase, launched }` and never rejects, so a caller can branch
 * on `ok` without a try/catch around the readiness step.
 *
 * @param {object=}        opts
 * @param {string=}        opts.baseUrl        desktop service URL
 * @param {function=}      opts.onStatus       ({ phase, text }) progress callback
 * @param {AbortSignal=}   opts.signal         gives up early when aborted
 * @param {number=}        opts.timeoutMs      total wait for a launch
 * @param {function=}      opts.launch         injection seam for tests
 */
export const ensureDesktopService = async (opts = {}) => {
  const {
    baseUrl = DESKTOP_SERVICE_URL,
    onStatus,
    signal,
    timeoutMs = DESKTOP_LAUNCH_TIMEOUT_MS,
    pollIntervalMs = DESKTOP_POLL_INTERVAL_MS,
    probeTimeoutMs = DESKTOP_PROBE_TIMEOUT_MS,
    launch = openDesktopApp,
  } = opts
  const url = normalizeDesktopUrl(baseUrl)

  if (signal?.aborted) {
    return result(false, DESKTOP_SERVICE_PHASE.canceled, false)
  }

  report(onStatus, DESKTOP_SERVICE_PHASE.probing)
  if (await pingDesktopService(url, probeTimeoutMs)) {
    report(onStatus, DESKTOP_SERVICE_PHASE.ready)
    return result(true, DESKTOP_SERVICE_PHASE.ready, false)
  }
  if (signal?.aborted) {
    return result(false, DESKTOP_SERVICE_PHASE.canceled, false)
  }

  const shared = pendingLaunches.get(url)
  const entry = shared || { listeners: new Set() }
  if (onStatus) entry.listeners.add(onStatus)
  if (!shared) {
    entry.promise = launchAndWait({
      url,
      listeners: entry.listeners,
      timeoutMs,
      pollIntervalMs,
      probeTimeoutMs,
      launch,
    }).finally(() => pendingLaunches.delete(url))
    pendingLaunches.set(url, entry)
  }

  // A caller that gives up stops waiting; the shared launch keeps going for
  // whoever else is still watching it.
  const abortRace = signal
    ? new Promise((resolve) => {
        signal.addEventListener(
          'abort',
          () => resolve(result(false, DESKTOP_SERVICE_PHASE.canceled, false)),
          { once: true }
        )
      })
    : null

  try {
    return abortRace
      ? await Promise.race([entry.promise, abortRace])
      : await entry.promise
  } catch (e) {
    return result(false, DESKTOP_SERVICE_PHASE.offline, true)
  } finally {
    if (onStatus) entry.listeners.delete(onStatus)
  }
}
